import * as amp from '@amplitude/analytics-browser';
import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';
import { configuration } from '@Common/Helpers/configuration';

type UserProperties = {
    account_status: string;
    authentication_status: string | null;
    from_CA: boolean;
    from_LCL: boolean;
    organisation_type: string | null;
    organization_active_member_count: string | null;
    organization_country: string;
    organization_employees: string | null;
    organization_id: string;
    organization_needs: string;
    organization_sector: string | null;
    organization_type: string;
    organization_user_count: string;
    plan_name: string;
    regional_bank: string;
    user_email: string;
    user_id: string;
    user_language: string;
};

declare global {
    interface GlobalEventHandlersEventMap {
        userPropertiesReady: CustomEvent<UserProperties>;
    }
}

export const setUserProperties = (userProps: UserProperties) => {
    const identity = new amp.Identify();

    for (const key in userProps) {
        if (key === 'organization_needs') {
            identity.set(key, JSON.parse(userProps[key as keyof UserProperties] as string) || {});
            continue;
        }
        identity.set(key, userProps[key as keyof UserProperties] || '');
    }

    amp.identify(identity);
};

let amplitudeLoaded = false;

let callbacks: (() => void)[] = [];

export const callWhenReady = (callback: () => void) => {
    if (amplitudeLoaded) {
        callback();
    } else {
        callbacks.push(callback);
    }
};

const callBacklog = () => {
    amplitudeLoaded = true;
    callbacks.forEach(callback => callback());
    callbacks = [];
};

const storageKey = 'automation';
export const loadUserProperties = async () => {
    if (!configuration.canTrackEvents) {
        // If canTrackEvents is false, just load experiments without setting user properties to avoid in-between rendering states
        await experiment?.fetch();
        callBacklog();
        return;
    }

    if (localStorage.getItem(storageKey)) {
        const userProperties = JSON.parse(localStorage.getItem(storageKey) || '[{}]');
        setUserProperties(userProperties[0]);

        await experiment?.fetch();
        callBacklog();
    } else {
        document.addEventListener('userPropertiesReady', async (event: CustomEvent<UserProperties>) => {
            setUserProperties(event.detail);

            await experiment?.fetch();
            callBacklog();
        });
    }
};

export let experiment: ExperimentClient | null = null;
export const loadAmplitude = () => {
    if (import.meta.env.VITE_AMPLITUDE_API_KEY) {
        amp.init(import.meta.env.VITE_AMPLITUDE_API_KEY, configuration.userId, {
            appVersion: import.meta.env.VITE_APP_VERSION || '',
            autocapture: {
                sessions: false,
                elementInteractions: false,
                fileDownloads: false,
                pageViews: false,
                formInteractions: false,
                attribution: true
            }
        });

        experiment = Experiment.initializeWithAmplitudeAnalytics(import.meta.env.VITE_AMPLITUDE_API_KEY);

        loadUserProperties();
    }
};